/// <reference path="../../types.d.ts" />
import { state } from "../core/state";
import { formatLifeYears } from "../core/helpers";
import { getParents, getSpousesAndChildren, getMaternalBranchInfo } from "../engine/graph";

export interface ShareCardRenderOptions {
  father: string | null;
  mother: string | null;
  spouses: string | null;
  children: string | null;
  personBranch: string | null;
  genNum: number;
}

export async function renderShareCardToCanvas(
  canvas: HTMLCanvasElement,
  p: PersonRecord,
  opts: ShareCardRenderOptions
): Promise<void> {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const W = 1080;
  const H = 1350;
  canvas.width = W;
  canvas.height = H;

  // 1. Background gradient
  const bgGrad = ctx.createLinearGradient(0, 0, 0, H);
  bgGrad.addColorStop(0, "#06130f");
  bgGrad.addColorStop(0.5, "#0f231a");
  bgGrad.addColorStop(1, "#07140f");
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, W, H);

  // 2. Subtle background pattern
  ctx.save();
  ctx.strokeStyle = "rgba(212, 175, 55, 0.04)";
  ctx.lineWidth = 1;
  for (let i = 40; i < W; i += 40) {
    ctx.beginPath();
    ctx.moveTo(i, 0);
    ctx.lineTo(i, H);
    ctx.stroke();
  }
  for (let j = 40; j < H; j += 40) {
    ctx.beginPath();
    ctx.moveTo(0, j);
    ctx.lineTo(W, j);
    ctx.stroke();
  }
  ctx.restore();

  // 3. Ornate Double Gold Border
  ctx.strokeStyle = "#d4af37";
  ctx.lineWidth = 3.5;
  ctx.strokeRect(36, 36, W - 72, H - 72);

  ctx.strokeStyle = "rgba(212, 175, 55, 0.4)";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(48, 48, W - 96, H - 96);

  function drawDiamond(cx: number, cy: number, size: number, color: string) {
    if (!ctx) return;
    ctx.save();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(cx, cy - size);
    ctx.lineTo(cx + size, cy);
    ctx.lineTo(cx, cy + size);
    ctx.lineTo(cx - size, cy);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  drawDiamond(36, 36, 9, "#d4af37");
  drawDiamond(W - 36, 36, 9, "#d4af37");
  drawDiamond(36, H - 36, 9, "#d4af37");
  drawDiamond(W - 36, H - 36, 9, "#d4af37");

  // 4. Header Section
  ctx.textAlign = "center";
  ctx.fillStyle = "#d4af37";
  ctx.font = "bold 20px Georgia, serif";
  ctx.fillText("✦  ⚜️  ✦", W / 2, 95);

  ctx.font = "bold 34px Georgia, serif";
  ctx.fillStyle = "#f3e3be";
  ctx.fillText("NİKSİÇ & HOCİÇ SÜLALESİ", W / 2, 142);

  ctx.font = "16px 'Segoe UI', sans-serif";
  ctx.fillStyle = "rgba(212, 175, 55, 0.85)";
  ctx.fillText("AİLE SOYAĞACI VE TARİHİ ARŞİVİ", W / 2, 176);

  ctx.strokeStyle = "rgba(212, 175, 55, 0.5)";
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(220, 205);
  ctx.lineTo(W - 220, 205);
  ctx.stroke();
  drawDiamond(W / 2, 205, 5, "#d4af37");

  // 5. Generation & Branch Pill
  const genText = `✦  ${opts.genNum}. KUŞAK${opts.personBranch ? ` • ${opts.personBranch.toUpperCase()}` : ""}  ✦`;
  ctx.font = "bold 17px 'Segoe UI', sans-serif";
  const pillW = ctx.measureText(genText).width + 44;
  const pillH = 34;
  const pillX = (W - pillW) / 2;
  const pillY = 230;

  ctx.fillStyle = "rgba(16, 185, 129, 0.16)";
  ctx.beginPath();
  ctx.roundRect(pillX, pillY, pillW, pillH, 17);
  ctx.fill();
  ctx.strokeStyle = "#d4af37";
  ctx.lineWidth = 1.2;
  ctx.stroke();

  ctx.fillStyle = "#d4af37";
  ctx.fillText(genText, W / 2, pillY + 23);

  // 6. Avatar / Photo
  const avatarCx = W / 2;
  const avatarCy = 425;
  const avatarR = 110;

  let photoLoaded = false;
  if (p.avatarUrl) {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      await new Promise((resolve) => {
        img.onload = () => {
          if (!ctx) return resolve(null);
          ctx.save();
          ctx.beginPath();
          ctx.arc(avatarCx, avatarCy, avatarR, 0, Math.PI * 2);
          ctx.closePath();
          ctx.clip();
          ctx.drawImage(img, avatarCx - avatarR, avatarCy - avatarR, avatarR * 2, avatarR * 2);
          ctx.restore();
          photoLoaded = true;
          resolve(null);
        };
        img.onerror = () => resolve(null);
        img.src = p.avatarUrl!;
      });
    } catch (_) {}
  }

  if (!photoLoaded) {
    const isFemale = p.sex === "F";
    const circleBg = isFemale ? "#701a75" : "#1e3a8a";
    const accentCol = isFemale ? "#f472b6" : "#60a5fa";

    ctx.save();
    ctx.fillStyle = circleBg;
    ctx.beginPath();
    ctx.arc(avatarCx, avatarCy, avatarR, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = accentCol;
    ctx.beginPath();
    ctx.arc(avatarCx, avatarCy - 24, 34, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.arc(avatarCx, avatarCy + 70, 68, Math.PI, 0, false);
    ctx.fill();
    ctx.restore();
  }

  ctx.strokeStyle = "#d4af37";
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(avatarCx, avatarCy, avatarR + 3, 0, Math.PI * 2);
  ctx.stroke();

  ctx.strokeStyle = "rgba(212, 175, 55, 0.4)";
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(avatarCx, avatarCy, avatarR + 9, 0, Math.PI * 2);
  ctx.stroke();

  // 7. Person Full Name
  ctx.fillStyle = "#ffffff";
  const nameLen = p.name.length;
  ctx.font = nameLen > 24 ? "bold 34px Georgia, serif" : "bold 44px Georgia, serif";
  ctx.fillText(p.name, W / 2, 600);

  // 8. Lifespan Badge
  const lifeText = `${formatLifeYears(p)} • ${p.deceased ? "Vefat Etmiş" : "Hayatta"}`;
  ctx.font = "bold 20px 'Segoe UI', sans-serif";
  const lifeW = ctx.measureText(lifeText).width + 36;
  const lifeH = 36;
  const lifeX = (W - lifeW) / 2;
  const lifeY = 632;

  ctx.fillStyle = "rgba(217, 119, 6, 0.22)";
  ctx.beginPath();
  ctx.roundRect(lifeX, lifeY, lifeW, lifeH, 18);
  ctx.fill();
  ctx.strokeStyle = "rgba(217, 119, 6, 0.7)";
  ctx.lineWidth = 1.2;
  ctx.stroke();

  ctx.fillStyle = "#fde68a";
  ctx.fillText(lifeText, W / 2, lifeY + 25);

  ctx.strokeStyle = "rgba(212, 175, 55, 0.35)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(140, 695);
  ctx.lineTo(W - 140, 695);
  ctx.stroke();
  drawDiamond(W / 2, 695, 4, "#d4af37");

  // 9. Lineage & Family Info Container
  const boxX = 85;
  const boxY = 720;
  const boxW = W - 170;
  const boxH = 485;

  ctx.fillStyle = "rgba(255, 255, 255, 0.035)";
  ctx.beginPath();
  ctx.roundRect(boxX, boxY, boxW, boxH, 16);
  ctx.fill();
  ctx.strokeStyle = "rgba(212, 175, 55, 0.3)";
  ctx.lineWidth = 1.2;
  ctx.stroke();

  const infoRows = [
    { icon: "👨", label: "BABA", val: opts.father || "Kayıt Yok / Bilinmiyor" },
    { icon: "👩", label: "ANNE", val: opts.mother || "Kayıt Yok / Bilinmiyor" },
    { icon: "💍", label: "EŞ(LER)", val: opts.spouses || "Bekar / Kayıt Yok" },
    { icon: "👶", label: "ÇOCUKLAR", val: opts.children || "Kayıt Yok" },
    { icon: "📍", label: "YER", val: [p.birthPlace ? `Doğum: ${p.birthPlace}` : "", p.deathPlace ? `Vefat: ${p.deathPlace}` : ""].filter(Boolean).join(" • ") || "Kayıt Yok" },
    { icon: "💼", label: "MESLEK", val: p.occupation || "Belirtilmemiş" },
  ];

  ctx.textAlign = "left";
  const rowStartY = boxY + 45;
  const rowGap = 72;

  infoRows.forEach((row, idx) => {
    if (!ctx) return;
    const curY = rowStartY + idx * rowGap;

    ctx.font = "24px 'Segoe UI', sans-serif";
    ctx.fillText(row.icon, boxX + 28, curY + 2);

    ctx.font = "bold 16px 'Segoe UI', sans-serif";
    ctx.fillStyle = "#d4af37";
    ctx.fillText(row.label, boxX + 70, curY);

    ctx.font = "20px 'Segoe UI', sans-serif";
    ctx.fillStyle = "#ffffff";
    const maxValW = boxW - 270;
    let valStr = String(row.val);
    while (ctx.measureText(valStr).width > maxValW && valStr.length > 5) {
      valStr = valStr.slice(0, -4) + "…";
    }
    ctx.fillText(valStr, boxX + 230, curY);

    if (idx < infoRows.length - 1) {
      ctx.strokeStyle = "rgba(255, 255, 255, 0.06)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(boxX + 28, curY + 24);
      ctx.lineTo(boxX + boxW - 28, curY + 24);
      ctx.stroke();
    }
  });

  // 10. Footer Section
  ctx.textAlign = "center";
  ctx.strokeStyle = "rgba(212, 175, 55, 0.5)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(160, 1238);
  ctx.lineTo(W - 160, 1238);
  ctx.stroke();
  drawDiamond(W / 2, 1238, 4, "#d4af37");

  ctx.font = "bold 21px Georgia, serif";
  ctx.fillStyle = "#d4af37";
  ctx.fillText("🌐 nh.ecemaya.com  •  Resmi Aile Portalı", W / 2, 1276);

  ctx.font = "14px 'Segoe UI', sans-serif";
  ctx.fillStyle = "rgba(255, 255, 255, 0.55)";
  ctx.fillText(`holovic.niksic.hocic  •  Kişi ID: #${p.id}  •  ${new Date().toLocaleDateString("tr-TR", { month: "long", year: "numeric" })}`, W / 2, 1306);
}

export function openShareCardModal(
  pid: string,
  openModal: (title: string, htmlContent: string, isWide?: boolean) => void
): void {
  const p = state.data?.individuals[pid];
  if (!p) return;

  const { father, mother } = getParents(pid);
  const unions = getSpousesAndChildren(pid);
  const personBranch = getMaternalBranchInfo(pid);
  const genNum = p.generation !== undefined ? p.generation + 1 : 1;

  const spouses = unions.filter((u) => u.spouse).map((u) => u.spouse!.name).join(", ");
  const allChildren: string[] = [];
  for (const u of unions) {
    for (const ch of u.children) allChildren.push(ch.name);
  }

  const modalHtml = `
    <div style="text-align: center; max-width: 440px; margin: 0 auto;">
      <p class="section-subtitle" style="margin-bottom: 0.75rem;">
        ${state.lang === "tr" ? "WhatsApp, Instagram ve sosyal medyada paylaşabileceğiniz yüksek çözünürlüklü aile soyağacı kartı." : "High-resolution family card for WhatsApp and social media."}
      </p>

      <div style="position: relative; margin: 0 auto; max-width: 340px; border-radius: 14px; overflow: hidden; box-shadow: 0 16px 40px rgba(0,0,0,0.6); border: 2px solid #d4af37;">
        <canvas id="share-card-canvas" width="1080" height="1350" style="width: 100%; height: auto; display: block;"></canvas>
        <div id="share-card-loading" style="position: absolute; inset: 0; background: rgba(10,20,16,0.9); display: flex; align-items: center; justify-content: center; color: #d4af37; font-weight: 600;">
          ⏳ ${state.lang === "tr" ? "Görsel Oluşturuluyor..." : "Rendering Card..."}
        </div>
      </div>

      <div style="display: flex; flex-direction: column; gap: 0.5rem; margin-top: 1rem;">
        <div style="display: flex; gap: 0.5rem; justify-content: center;">
          <button type="button" class="primary-btn" id="download-share-card-btn" style="flex: 1; padding: 0.75rem 1rem;">
            📥 ${state.lang === "tr" ? "Görseli İndir (PNG)" : "Download PNG"}
          </button>
          <button type="button" class="primary-btn" id="whatsapp-share-card-btn" style="flex: 1; padding: 0.75rem 1rem; background: #25d366; border-color: #25d366; color: #ffffff;">
            📲 ${state.lang === "tr" ? "WhatsApp Paylaş" : "Share WhatsApp"}
          </button>
        </div>
        <button type="button" class="mini-btn" id="copy-share-card-text-btn" style="padding: 0.5rem; width: 100%;">
          📋 ${state.lang === "tr" ? "Akraba Bilgilerini Metin Olarak Kopyala" : "Copy Info as Text"}
        </button>
        <div id="share-card-status" style="font-size: 0.8rem; color: #34d399; min-height: 1.2rem;"></div>
      </div>
    </div>
  `;

  openModal(`📲 ${state.lang === "tr" ? "Akraba Kartı" : "Family Card"}: ${p.name}`, modalHtml, false);

  setTimeout(() => {
    const canvas = document.getElementById("share-card-canvas") as HTMLCanvasElement | null;
    const loading = document.getElementById("share-card-loading");
    if (!canvas) return;

    renderShareCardToCanvas(canvas, p, {
      father: father ? father.name : null,
      mother: mother ? mother.name : null,
      spouses: spouses || null,
      children: allChildren.length > 0 ? (allChildren.length <= 3 ? allChildren.join(", ") : `${allChildren.slice(0, 3).join(", ")} (+${allChildren.length - 3})`) : null,
      personBranch: personBranch ? personBranch.label : null,
      genNum,
    }).then(() => {
      if (loading) loading.style.display = "none";
    });

    document.getElementById("download-share-card-btn")?.addEventListener("click", () => {
      canvas.toBlob((blob) => {
        if (!blob) return;
        const link = document.createElement("a");
        const safeName = p.name.replace(/[^a-zA-Z0-9çğıöşüÇĞİÖŞÜ]/g, "_");
        link.download = `Niksic_Hocic_${safeName}_Akraba_Karti.png`;
        link.href = URL.createObjectURL(blob);
        link.click();
        showShareStatus(state.lang === "tr" ? "Görsel indirildi!" : "Image downloaded!");
      }, "image/png");
    });

    document.getElementById("whatsapp-share-card-btn")?.addEventListener("click", async () => {
      const shareText = `*${p.name}* (${formatLifeYears(p)})\n` +
        `Niksiç & Hociç Sülalesi • ${genNum}. Kuşak\n` +
        (father ? `Baba: ${father.name}\n` : "") +
        (mother ? `Anne: ${mother.name}\n` : "") +
        `🌐 Soyağacı Portalı: https://nh.ecemaya.com/#person=${p.id}`;

      if (navigator.share && (navigator as any).canShare) {
        try {
          canvas.toBlob(async (blob) => {
            if (!blob) return;
            const file = new File([blob], `${p.name}_Karti.png`, { type: "image/png" });
            if ((navigator as any).canShare({ files: [file] })) {
              await navigator.share({
                title: `${p.name} - Niksiç & Hociç Soy Ağacı`,
                text: shareText,
                files: [file],
              });
              showShareStatus(state.lang === "tr" ? "Paylaşıldı!" : "Shared!");
              return;
            }
            await navigator.share({
              title: `${p.name} - Niksiç & Hociç Soy Ağacı`,
              text: shareText,
              url: `https://nh.ecemaya.com/#person=${p.id}`,
            });
          }, "image/png");
          return;
        } catch (e: any) {
          if (e.name === "AbortError") return;
        }
      }

      const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
      window.open(waUrl, "_blank");
      showShareStatus(state.lang === "tr" ? "WhatsApp açıldı!" : "WhatsApp opened!");
    });

    document.getElementById("copy-share-card-text-btn")?.addEventListener("click", () => {
      const shareText = `*${p.name}* (${formatLifeYears(p)})\n` +
        `Niksiç & Hociç Sülalesi • ${genNum}. Kuşak\n` +
        (father ? `Baba: ${father.name}\n` : "") +
        (mother ? `Anne: ${mother.name}\n` : "") +
        (spouses ? `Eş: ${spouses}\n` : "") +
        (p.birthPlace ? `Doğum Yeri: ${p.birthPlace}\n` : "") +
        (p.occupation ? `Meslek: ${p.occupation}\n` : "") +
        `🌐 https://nh.ecemaya.com/#person=${p.id}`;
      navigator.clipboard.writeText(shareText);
      showShareStatus(state.lang === "tr" ? "Metin panoya kopyalandı!" : "Copied to clipboard!");
    });
  }, 50);

  function showShareStatus(msg: string) {
    const el = document.getElementById("share-card-status");
    if (!el) return;
    el.textContent = msg;
    setTimeout(() => {
      if (el) el.textContent = "";
    }, 2500);
  }
}
